import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Info } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { PRODUCTOS, COMPLEJIDADES, ORIGENES } from "./_common";

export default function SolicitudNueva() {
    const navigate = useNavigate();
    const [sending, setSending] = useState(false);
    const [form, setForm] = useState({
        origen: "referencia_directa",
        solicitante: {
            razon_social: "", rfc: "",
            contacto_nombre: "", contacto_email: "", contacto_telefono: "",
        },
        producto_solicitado: "P-GAR",
        patrimonio_estimado_mxn: "",
        moneda: "MXN",
        descripcion_negocio: "",
        complejidad: "estandar",
    });

    const upd = (path, value) => {
        setForm((prev) => {
            if (path.includes(".")) {
                const [a, b] = path.split(".");
                return { ...prev, [a]: { ...prev[a], [b]: value } };
            }
            return { ...prev, [path]: value };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        const s = form.solicitante;
        if (!s.razon_social || !s.rfc || !s.contacto_nombre
            || !s.contacto_email || !s.contacto_telefono) {
            toast.error("Completa los datos del solicitante");
            return;
        }
        if (!form.patrimonio_estimado_mxn || Number(form.patrimonio_estimado_mxn) <= 0) {
            toast.error("Patrimonio estimado debe ser mayor a 0");
            return;
        }
        if (form.descripcion_negocio.length < 10) {
            toast.error("Descripción del negocio: mínimo 10 caracteres");
            return;
        }
        setSending(true);
        try {
            const payload = {
                ...form,
                patrimonio_estimado_mxn: Number(form.patrimonio_estimado_mxn),
            };
            const { data } = await api.post("/aceptacion/pipeline/solicitudes", payload);
            toast.success("Solicitud creada", { description: `Folio ${data.folio}` });
            navigate(`/aceptacion/solicitudes/${data.folio}`);
        } catch (err) {
            toast.error("No se pudo crear la solicitud", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="p-8 lg:p-10 max-w-4xl space-y-6" data-testid="solicitud-nueva-page">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-4 w-4 mr-1" /> Volver
                </Button>
            </div>
            <div>
                <h1 className="font-display text-3xl text-[color:var(--brand-charcoal)]">
                    Nueva solicitud · F-01
                </h1>
                <p className="text-sm text-[color:var(--brand-charcoal)]/70 mt-1">
                    Captura inicial del expediente de aceptación G-02. Al guardar se asigna folio y
                    comienza el conteo del SLA.
                </p>
            </div>

            <form onSubmit={submit} className="space-y-6" data-testid="solicitud-form">
                {/* Origen */}
                <section className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                    <h2 className="font-display text-lg">Origen de la solicitud</h2>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-gray-600">Origen *</label>
                        <Select value={form.origen} onValueChange={(v) => upd("origen", v)}>
                            <SelectTrigger className="mt-1" data-testid="form-origen"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {ORIGENES.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </section>

                {/* Solicitante */}
                <section className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                    <h2 className="font-display text-lg">Solicitante</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Razón social *">
                            <Input value={form.solicitante.razon_social}
                                   onChange={(e) => upd("solicitante.razon_social", e.target.value)}
                                   required data-testid="form-razon-social" />
                        </Field>
                        <Field label="RFC *">
                            <Input value={form.solicitante.rfc}
                                   onChange={(e) => upd("solicitante.rfc", e.target.value.toUpperCase())}
                                   maxLength={13} required data-testid="form-rfc" />
                        </Field>
                        <Field label="Nombre de contacto *">
                            <Input value={form.solicitante.contacto_nombre}
                                   onChange={(e) => upd("solicitante.contacto_nombre", e.target.value)}
                                   required data-testid="form-contacto-nombre" />
                        </Field>
                        <Field label="Email de contacto *">
                            <Input type="email" value={form.solicitante.contacto_email}
                                   onChange={(e) => upd("solicitante.contacto_email", e.target.value)}
                                   required data-testid="form-contacto-email" />
                        </Field>
                        <Field label="Teléfono de contacto *">
                            <Input value={form.solicitante.contacto_telefono}
                                   onChange={(e) => upd("solicitante.contacto_telefono", e.target.value)}
                                   required data-testid="form-contacto-telefono" />
                        </Field>
                    </div>
                </section>

                {/* Producto & Negocio */}
                <section className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
                    <h2 className="font-display text-lg">Producto y negocio</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Producto solicitado *">
                            <Select value={form.producto_solicitado}
                                    onValueChange={(v) => upd("producto_solicitado", v)}>
                                <SelectTrigger className="mt-1" data-testid="form-producto"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PRODUCTOS.map((p) => (
                                        <SelectItem key={p.code} value={p.code}>
                                            {p.code} · {p.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Complejidad *" hint="Estándar = 10 días hábiles · Compleja = 20 días hábiles.">
                            <Select value={form.complejidad}
                                    onValueChange={(v) => upd("complejidad", v)}>
                                <SelectTrigger className="mt-1" data-testid="form-complejidad"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {COMPLEJIDADES.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Patrimonio estimado (MXN) *">
                            <Input type="number" min="0" step="100000"
                                   value={form.patrimonio_estimado_mxn}
                                   onChange={(e) => upd("patrimonio_estimado_mxn", e.target.value)}
                                   required data-testid="form-patrimonio" />
                        </Field>
                        <Field label="Moneda">
                            <Input value={form.moneda} onChange={(e) => upd("moneda", e.target.value.toUpperCase())}
                                   maxLength={3} />
                        </Field>
                    </div>
                    <Field label="Descripción del negocio *" hint="Mínimo 10 caracteres. Incluye estructura, contrapartes y objetivo.">
                        <textarea
                            value={form.descripcion_negocio}
                            onChange={(e) => upd("descripcion_negocio", e.target.value)}
                            className="w-full min-h-[140px] rounded-md border border-gray-300 px-3 py-2 text-sm"
                            maxLength={3000}
                            minLength={10}
                            required
                            data-testid="form-descripcion"
                        />
                        <div className="text-[10px] text-gray-400 text-right mt-1">
                            {form.descripcion_negocio.length}/3000
                        </div>
                    </Field>
                </section>

                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancelar</Button>
                    <Button type="submit" disabled={sending} data-testid="form-submit">
                        <Save className="h-4 w-4 mr-2" />
                        {sending ? "Guardando…" : "Crear solicitud"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider text-gray-600">{label}</label>
            {children}
            {hint && (
                <div className="mt-1 text-[11px] text-gray-500 flex items-start gap-1">
                    <Info className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>{hint}</span>
                </div>
            )}
        </div>
    );
}
